import React from 'react';
import { FileText, RefreshCw, HelpCircle, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { StatusPillType } from '@/hooks/useGlobalStatus';
import { AuthButton } from '@/components/common/AuthButton';
import { ComparisonModal } from '@/components/common/ComparisonModal';
import { HelpDialog } from '@/components/common/HelpDialog';
import { useAuth } from '@/contexts/AuthContext';
import { AppMode } from '@/hooks/useWorkspacesState';

import { ExtensionPromo } from '@/components/common/ExtensionPromo';

interface HeaderProps {
  status: StatusPillType;
  resetWorkspace: () => void;
  onHome?: (e: React.MouseEvent) => void;
  appMode?: AppMode;
}

export function Header({ status, resetWorkspace, onHome, appMode }: HeaderProps) {
  const { user } = useAuth();

  return (
    <header className="shrink-0 w-full border-b border-slate-300 dark:border-slate-800 bg-white dark:bg-slate-950 flex items-center justify-between px-4 py-3 lg:px-6 z-50 sticky top-0 min-h-[3.5rem]">
      <Link to="/" aria-label="Go to homepage" className="flex items-center gap-2.5 cursor-pointer group hover:opacity-80 transition-opacity">
        <div className="bg-emerald-600 text-white dark:bg-emerald-500 dark:text-emerald-950 p-1.5 rounded-md dark:shadow-sm group-hover:scale-105 transition-transform">
          <FileText className="w-4 h-4" strokeWidth={2.5} />
        </div>
        <h1 className="text-base font-bold tracking-tight text-emerald-800 dark:text-emerald-400 hidden sm:block">Resume<span className="text-emerald-600/70 dark:text-emerald-300/70 font-medium">Pro</span></h1>
      </Link>
      
      <div className="flex items-center gap-2 sm:gap-3 md:gap-4 lg:gap-6">
        {appMode && appMode !== 'initial' && <HelpDialog appMode={appMode} />}
        <ComparisonModal />
        <ExtensionPromo />
        <div className="hidden md:block h-4 w-px bg-slate-200 dark:bg-slate-800"></div>

        <AuthButton />

        {user && (
          <>
            <div className="hidden md:block h-4 w-px bg-slate-200 dark:bg-slate-800"></div>
          </>
        )}

        <button
          onClick={resetWorkspace}
          disabled={status === 'working'}
          aria-label="Reset workspace"
          className="flex items-center gap-1.5 text-xs font-medium text-slate-700 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed h-8 px-3 rounded-md"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Reset Workspace</span>
        </button>
        
        <div className="hidden sm:block h-4 w-px bg-slate-200 dark:bg-slate-800"></div>
        <div className="hidden sm:block">
          <ThemeToggle />
        </div>
      </div>
    </header>
  );
}
